/**
 * GraphQL URL utility for handling build-time vs runtime URLs
 *
 * GRAPHQL_URL: Used during build time (static generation, codegen, etc.)
 * GRAPHQL_URL_SERVER: Used during runtime (API routes, server components at runtime)
 */

/**
 * Determines if the current execution is happening during build time
 */
export const isBuildTime = (): boolean => {
  // During build, Next.js sets these environment variables
  return (
    process.env.NEXT_PHASE === "phase-production-build" ||
    process.env.NEXT_PHASE === "phase-development-build" ||
    process.env.npm_lifecycle_event === "build" ||
    (process.env.NODE_ENV === "production" &&
      typeof window === "undefined" &&
      !process.env.VERCEL_ENV &&
      !process.env.NEXT_RUNTIME)
  )
}

const buildUrl = process.env.GRAPHQL_URL

/**
 * Gets the appropriate GraphQL URL based on execution context
 */
export const getGraphQLUrl = (): string => {
  if (isBuildTime()) {
    // Use build-time URL for static generation, codegen, etc.

    if (!buildUrl) {
      console.error(
        "GRAPHQL_URL environment variable is missing during build time",
      )
      throw new Error("GRAPHQL_URL is required during build time")
    }
    return buildUrl
  }

  // Use runtime URL for server/client operations
  const runtimeUrl = process.env.GRAPHQL_URL_SERVER || process.env.GRAPHQL_URL
  if (!runtimeUrl) {
    console.error("GraphQL URL environment variables missing at runtime:")
    console.error("GRAPHQL_URL_SERVER:", process.env.GRAPHQL_URL_SERVER)
    console.error("GRAPHQL_URL:", process.env.GRAPHQL_URL)
    throw new Error(
      "GRAPHQL_URL_SERVER or GRAPHQL_URL is required during runtime",
    )
  }
  return runtimeUrl
}

/**
 * Gets the GraphQL URL without the '/graphql' suffix
 * Useful for REST API endpoints that share the same base URL
 */
export const getBaseUrl = (useRuntime: boolean = true): string => {
  const url = useRuntime ? getGraphQLUrl() : process.env.GRAPHQL_URL || ""
  return url.replace("/graphql", "").replace(/\/$/, "")
}

/**
 * Gets the build-time GraphQL URL specifically
 */
export const getBuildTimeGraphQLUrl = (): string => {
  const url = process.env.GRAPHQL_URL
  if (!url) {
    throw new Error(
      "GRAPHQL_URL environment variable is required for build-time operations",
    )
  }
  return url
}

/**
 * Gets the runtime GraphQL URL specifically
 */
export const getRuntimeGraphQLUrl = (): string => {
  const url = process.env.GRAPHQL_URL_SERVER
  if (!url) {
    throw new Error(
      "GRAPHQL_URL_SERVER environment variable is required for runtime operations",
    )
  }
  return url
}
